import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import type { UnitTourMap } from './types';

interface UnitTourMiniMapProps {
  map: UnitTourMap;
  currentPhotoId: number | null;
}

const SMALL = { w: 168, h: 126 };
const PAD_RATIO = 0.12;

/**
 * A floor-plan mini map cropped to the unit's area-tag polygon, showing the
 * marker for the panorama the user is currently viewing.
 *
 * The crop is done entirely with a CSS transform driven by the polygon
 * coordinates (which share the floor-plan image's pixel space), so it does not
 * depend on measuring the image's natural size.
 */
const UnitTourMiniMap: React.FC<UnitTourMiniMapProps> = ({ map, currentPhotoId }) => {
  const [expanded, setExpanded] = useState(false);
  const [viewport, setViewport] = useState(() => ({
    w: typeof window !== 'undefined' ? window.innerWidth : 1024,
    h: typeof window !== 'undefined' ? window.innerHeight : 768,
  }));

  useEffect(() => {
    const onResize = () => setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!expanded) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setExpanded(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expanded]);

  const box = expanded
    ? {
        w: Math.round(Math.min(viewport.w * 0.92, 1100)),
        h: Math.round(Math.min(viewport.h * 0.82, 820)),
      }
    : SMALL;

  // Crop box (in image pixel coords) covering the area polygon AND every
  // marker, so a marker that sits just outside the drawn polygon is still
  // visible (and never clipped) in the cropped view.
  const bbox = useMemo(() => {
    const points: number[][] = [
      ...(map.polygon ?? []),
      ...map.markers.map((m) => [m.x, m.y]),
    ];
    if (points.length < 3) return null;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const [px, py] of points) {
      minX = Math.min(minX, px);
      minY = Math.min(minY, py);
      maxX = Math.max(maxX, px);
      maxY = Math.max(maxY, py);
    }
    const padX = Math.max((maxX - minX) * PAD_RATIO, 40);
    const padY = Math.max((maxY - minY) * PAD_RATIO, 40);
    return {
      x1: Math.max(0, minX - padX),
      y1: Math.max(0, minY - padY),
      x2: maxX + padX,
      y2: maxY + padY,
    };
  }, [map.polygon, map.markers]);

  const transform = useMemo(() => {
    if (!bbox) return null;
    const bw = Math.max(1, bbox.x2 - bbox.x1);
    const bh = Math.max(1, bbox.y2 - bbox.y1);
    const scale = Math.min(box.w / bw, box.h / bh);
    const offsetX = (box.w - bw * scale) / 2 - bbox.x1 * scale;
    const offsetY = (box.h - bh * scale) / 2 - bbox.y1 * scale;
    return { scale, offsetX, offsetY };
  }, [bbox, box.w, box.h]);

  const project = (x: number, y: number) =>
    transform
      ? { left: x * transform.scale + transform.offsetX, top: y * transform.scale + transform.offsetY }
      : { left: 0, top: 0 };

  const polygonPoints = useMemo(() => {
    if (!transform || (map.polygon ?? []).length < 3) return '';
    return map.polygon
      .map(([px, py]) => {
        const { left, top } = project(px, py);
        return `${left},${top}`;
      })
      .join(' ');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transform, map.polygon]);

  // Only the marker for the photo currently being viewed.
  const activeMarker = useMemo(
    () =>
      currentPhotoId == null
        ? null
        : map.markers.find((marker) => marker.photo_ids.includes(currentPhotoId)) ?? null,
    [map.markers, currentPhotoId],
  );

  const activePos = activeMarker ? project(activeMarker.x, activeMarker.y) : null;

  const mapBody = (
    <div className="relative overflow-hidden bg-white" style={{ width: box.w, height: box.h }}>
      {transform ? (
        <div
          className="absolute left-0 top-0"
          style={{
            transformOrigin: 'top left',
            transform: `translate(${transform.offsetX}px, ${transform.offsetY}px) scale(${transform.scale})`,
          }}
        >
          <img
            src={map.image_url}
            alt="Unit map"
            draggable={false}
            style={{ display: 'block', maxWidth: 'none' }}
            onError={() => console.error('[UnitTourMiniMap] map image failed', map.image_url)}
          />
        </div>
      ) : (
        <img
          src={map.image_url}
          alt="Unit map"
          draggable={false}
          className="h-full w-full object-contain"
        />
      )}

      {transform && polygonPoints && (
        <svg className="pointer-events-none absolute inset-0" width={box.w} height={box.h}>
          <polygon
            points={polygonPoints}
            fill="rgba(59,130,246,0.12)"
            stroke="rgba(59,130,246,0.85)"
            strokeWidth={1.5}
          />
        </svg>
      )}

      {activePos && (
        <div
          className="absolute -translate-x-1/2 -translate-y-1/2"
          style={{ left: activePos.left, top: activePos.top }}
        >
          <span className="absolute -inset-1.5 rounded-full bg-blue-400/50 animate-ping" />
          <span className="relative flex h-4 min-w-4 items-center justify-center rounded-full border border-blue-200 bg-blue-500 px-0.5 text-[9px] font-semibold leading-none text-white">
            {activeMarker?.number}
          </span>
        </div>
      )}
    </div>
  );

  if (expanded) {
    return (
      <div
        className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/70 p-4"
        onClick={() => setExpanded(false)}
      >
        <div
          className="relative overflow-hidden rounded-xl border border-white/30 bg-black/70 shadow-2xl backdrop-blur"
          style={{ width: box.w }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-3 py-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-white/80">
              Unit map
            </span>
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="flex items-center gap-1.5 rounded-md border border-white/30 bg-white/10 px-3 py-1 text-xs font-medium text-white transition-colors hover:bg-white/20"
            >
              <X className="h-3.5 w-3.5" />
              Close
            </button>
          </div>
          {mapBody}
        </div>
      </div>
    );
  }

  return (
    <div
      className="absolute right-3 top-3 z-[1000] overflow-hidden rounded-lg border border-white/30 bg-black/60 shadow-lg backdrop-blur"
      style={{ width: box.w }}
    >
      <div className="flex items-center justify-between px-2 py-1">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-white/80">
          Unit map
        </span>
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="rounded px-1.5 py-0.5 text-[10px] font-medium text-white/80 hover:bg-white/10 hover:text-white"
        >
          Expand
        </button>
      </div>
      {mapBody}
    </div>
  );
};

export default UnitTourMiniMap;
